"use client";

/**
 * Custom next/image loader: build-time WebP variants.
 *
 * Cloudflare Workers cannot run Next's image optimiser (no sharp), so
 * `/_next/image` used to hand back the untouched source file at every
 * requested width. scripts/generate-image-variants.ts pre-renders a WebP
 * width ladder into /images/gen and writes image-variants.generated.ts;
 * this loader resolves each next/image request against that manifest.
 *
 * A source with no manifest entry (remote URL, SVG, or an asset added
 * without re-running the generator) falls back to the original path, so a
 * missing variant degrades to "unoptimised" rather than to a 404.
 */
import { IMAGE_VARIANTS } from "./image-variants.generated";

function variantUrl(src: string, width: number) {
  const rel = src.replace(/^\/images\//, "");
  const slash = rel.lastIndexOf("/");
  const dir = slash === -1 ? "" : rel.slice(0, slash + 1);
  const file = rel.slice(slash + 1).replace(/\.[^.]+$/, "");
  return `/images/gen/${dir}${file}-${width}.webp`;
}

export default function staticImageLoader({
  src,
  width,
}: {
  src: string;
  width: number;
  quality?: number;
}): string {
  const widths = IMAGE_VARIANTS[src];
  if (!widths || widths.length === 0) return src;
  // Smallest rung that still covers the requested width; the widest rung
  // when the request exceeds the ladder (i.e. the source's own width).
  const chosen = widths.find((w) => w >= width) ?? widths[widths.length - 1];
  return variantUrl(src, chosen);
}
